// @flow
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import { toDateInTz, startOfDayInTz, isOpenDateMissing } from './utils';
import type { WaitlistV2Values, PreferenceItem } from './types';
import useIntl from '../../../../hooks/useIntl';
import messages from './messages';

export const PREFERENCE_RANKING_METHOD = {
  TOTAL_POINTS: 'TOTAL_POINTS',
  HIGHEST_PREFERENCE_WEIGHT: 'HIGHEST_PREFERENCE_WEIGHT',
};

// Canonical list of preference IDs with their zero-value defaults.
// Used as a fallback when the server hasn't persisted preferences yet.
const DEFAULT_PREFERENCES: PreferenceItem[] = [
  { id: 'veteran', weight: 0, enabled: false },
  { id: 'fosterCare', weight: 0, enabled: false },
  { id: 'involuntarilyDisplaced', weight: 0, enabled: false },
  { id: 'disability', weight: 0, enabled: false },
  { id: 'elderly', weight: 0, enabled: false },
  { id: 'homeless', weight: 0, enabled: false },
  { id: 'rentBurden', weight: 0, enabled: false },
  { id: 'residency', weight: 0, enabled: false },
  { id: 'victim', weight: 0, enabled: false },
  { id: 'workingFamilies', weight: 0, enabled: false },
];

export function useWaitlistV2(
  values: WaitlistV2Values,
  onChange: Function,
  editMode: boolean,
): Object {
  const intl = useIntl();
  const {
    affordableProgramOptions,
    selectedAffordablePrograms,
    affordableWaitlistEnabled,
    affordableWaitlistOpen,
    separateByBedroom,
    usePreferences,
    preferenceWeightMethod,
    openDate,
    closeDate,
    timezone,
    preferences,
  } = values;

  // Stable key for the preferences array — avoids re-running memos on
  // same-content but new-reference arrays from parent renders.
  const preferencesKey = (preferences ?? [])
    .map((p) => `${p.id}:${p.weight}:${String(p.enabled)}`)
    .join(',');

  const resolvedPreferences = useMemo(
    () =>
      DEFAULT_PREFERENCES.map((defaultItem) => {
        const serverItem = (preferences ?? []).find(
          (p) => p.id === defaultItem.id,
        );
        return serverItem ?? defaultItem;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preferencesKey],
  );

  // UI-ready options: isChecked mirrors the stored `enabled` field.
  const preferencesOptions = useMemo(
    () =>
      resolvedPreferences.map((item) => ({
        ...item,
      })),
    [resolvedPreferences],
  );

  // Local state for immediate UI response, mirroring the controlled value
  const [localPrograms, setLocalPrograms] = useState(
    selectedAffordablePrograms,
  );

  const [localWeightMethod, setLocalWeightMethod] = useState(
    preferenceWeightMethod ?? null,
  );

  // Validation error for the close date input — drives the error/helperText
  // props on the DatePicker so it renders consistently with other fields.
  const [closeDateError, setCloseDateError] = useState(null);

  // Derived, not stored: a latched flag could never be released once the
  // section was hidden, which left the page's Save button stuck. The page
  // evaluates the same predicate itself for the Save gate.
  const openDateError = isOpenDateMissing(
    affordableWaitlistEnabled,
    affordableWaitlistOpen,
    openDate,
  )
    ? intl.formatMessage(messages.openDateRequired)
    : null;

  useEffect(() => {
    setLocalWeightMethod(preferenceWeightMethod ?? null);
  }, [preferenceWeightMethod]);

  // Sync only when the actual selected values change, not on every render that
  // produces a new array reference (which would immediately undo user removals).
  const programsKey = selectedAffordablePrograms.map((p) => p.value).join(',');
  useEffect(() => {
    setLocalPrograms(selectedAffordablePrograms);
    // programsKey is the stable proxy for affordablePrograms content
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programsKey]);

  const selectComponents = useMemo(
    () => ({
      ClearIndicator: null,
      ...(editMode
        ? {}
        : { DropdownIndicator: null, IndicatorSeparator: null }),
    }),
    [editMode],
  );

  const handleAffordableProgramsChange = useCallback(
    (newValue) => {
      const nextPrograms = Array.isArray(newValue) ? newValue : [];
      setLocalPrograms(nextPrograms);
      onChange({
        target: {
          name: 'setup.waitlistV2.programs',
          value: nextPrograms.map((opt) => opt?.value).filter(Boolean),
        },
      });
    },
    [onChange],
  );

  const handleOpenWaitlistChange = useCallback(
    (evt) => {
      const isOpening = Boolean(evt.target.value);
      onChange({
        target: {
          name: 'setup.affordableWaitlistStatus',
          value: isOpening ? 'OPEN' : 'CLOSED',
        },
      });

      // When switching to OPEN, a past close date is no longer meaningful —
      // clear it so the user can set a new future close date if desired.
      if (isOpening && closeDate && moment(closeDate).isBefore(moment())) {
        onChange({
          target: { name: 'setup.waitlistV2.scheduledCloseDate', value: null },
        });
        setCloseDateError(null);
        onChange({
          target: { name: 'setup.waitlistV2.closeDateError', value: false },
        });
      }
    },
    [onChange, closeDate],
  );

  const handleOpenDateChange = useCallback(
    (date) => {
      const newOpenDate = date
        ? startOfDayInTz(formatDateDB(date), timezone)
        : null;

      onChange({
        target: {
          name: 'setup.waitlistV2.scheduledOpenDate',
          value: newOpenDate,
        },
      });

      // If the open date is cleared, the close date is no longer meaningful —
      // clear it as well so the user must re-select both.
      if (!newOpenDate && closeDate) {
        onChange({
          target: { name: 'setup.waitlistV2.scheduledCloseDate', value: null },
        });
        setCloseDateError(null);
        onChange({
          target: { name: 'setup.waitlistV2.closeDateError', value: false },
        });
        return;
      }

      if (newOpenDate && closeDate && newOpenDate >= closeDate) {
        onChange({
          target: { name: 'setup.waitlistV2.scheduledCloseDate', value: null },
        });
        setCloseDateError(null);
        onChange({
          target: { name: 'setup.waitlistV2.closeDateError', value: false },
        });
      }
    },
    [onChange, timezone, closeDate],
  );

  // Close date must always be strictly after open date.
  // When the waitlist is OPEN, the close date cannot be in the past either —
  // the earliest selectable date is tomorrow (or openDate + 1 if that's later).
  // When the waitlist is CLOSED, backdating the close date is allowed as long
  // as it remains after the open date.
  const closeDateMinDate = useMemo(
    () =>
      openDate
        ? affordableWaitlistOpen
          ? moment.max(
              moment(toDateInTz(openDate, timezone), 'YYYY-MM-DD').add(
                1,
                'day',
              ),
              moment().add(1, 'day').startOf('day'),
            )
          : moment(toDateInTz(openDate, timezone), 'YYYY-MM-DD').add(1, 'day')
        : undefined,
    [openDate, affordableWaitlistOpen, timezone],
  );

  const handleCloseDateChange = useCallback(
    (date) => {
      if (!date) {
        onChange({
          target: { name: 'setup.waitlistV2.scheduledCloseDate', value: null },
        });
        setCloseDateError(null);
        onChange({
          target: { name: 'setup.waitlistV2.closeDateError', value: false },
        });
        return;
      }

      if (!date.isValid?.()) {
        setCloseDateError('Close date must be a valid date.');
        onChange({
          target: { name: 'setup.waitlistV2.closeDateError', value: true },
        });
        return;
      }

      if (closeDateMinDate && date.isBefore(closeDateMinDate, 'day')) {
        setCloseDateError('Close date must be after the open date.');
        onChange({
          target: { name: 'setup.waitlistV2.closeDateError', value: true },
        });
        return;
      }

      setCloseDateError(null);
      onChange({
        target: { name: 'setup.waitlistV2.closeDateError', value: false },
      });
      onChange({
        target: {
          name: 'setup.waitlistV2.scheduledCloseDate',
          value: startOfDayInTz(formatDateDB(date), timezone),
        },
      });
    },
    [onChange, timezone, closeDateMinDate],
  );

  const handleSeparateByBedroomChange = useCallback(
    (evt) => {
      onChange({
        target: {
          name: 'setup.waitlistV2.separateByBedroom',
          value: evt.target.value,
        },
      });
    },
    [onChange],
  );

  const handleUsePreferencesChange = useCallback(
    (evt) => {
      const enabled = evt.target.value;
      onChange({
        target: {
          name: 'setup.waitlistV2.usePreferencesForRanking',
          value: enabled,
        },
      });
      if (enabled) {
        // Default to TOTAL_POINTS the first time preferences are enabled
        if (!localWeightMethod) {
          const defaultMethod = PREFERENCE_RANKING_METHOD.TOTAL_POINTS;
          setLocalWeightMethod(defaultMethod);
          onChange({
            target: {
              name: 'setup.waitlistV2.preferenceWeightMethod',
              value: defaultMethod,
            },
          });
        }
      } else {
        // Clear when preferences are disabled
        setLocalWeightMethod(null);
        onChange({
          target: {
            name: 'setup.waitlistV2.preferenceWeightMethod',
            value: null,
          },
        });
      }
    },
    [onChange, localWeightMethod],
  );

  const handlePreferenceWeightMethodChange = useCallback(
    (option) => {
      const value = option?.value ?? null;
      setLocalWeightMethod(value);
      onChange({
        target: {
          name: 'setup.waitlistV2.preferenceWeightMethod',
          value,
        },
      });
    },
    [onChange],
  );

  // Updates a single field ('enabled' or 'value') on one preference item
  // and fires onChange with the full updated array.
  const handlePreferenceChange = useCallback(
    (id, field, newValue) => {
      const updatedPreferences = resolvedPreferences.map((item) =>
        item.id === id ? { ...item, [field]: newValue } : item,
      );
      onChange({
        target: {
          name: 'setup.waitlistV2.preferences',
          value: updatedPreferences,
        },
      });
    },
    [onChange, resolvedPreferences],
  );

  // DatePicker values resolved to the property timezone
  const openDateValue = openDate
    ? moment(toDateInTz(openDate, timezone), 'YYYY-MM-DD')
    : null;

  const closeDateValue = closeDate
    ? moment(toDateInTz(closeDate, timezone), 'YYYY-MM-DD')
    : null;

  // Read-only display strings
  const openDateDisplay = openDate ? toDateInTz(openDate, timezone) : null;

  const closeDateDisplay = closeDate ? toDateInTz(closeDate, timezone) : null;

  return {
    // Values
    affordableProgramOptions,
    affordableWaitlistEnabled,
    affordableWaitlistOpen,
    separateByBedroom,
    usePreferences,
    preferenceWeightMethod: localWeightMethod,
    localPrograms,
    selectComponents,
    preferencesOptions,
    // Date picker / display
    openDateValue,
    closeDateValue,
    closeDateMinDate,
    closeDateError,
    openDateError,
    openDateDisplay,
    closeDateDisplay,
    // Handlers
    handleAffordableProgramsChange,
    handleOpenWaitlistChange,
    handleOpenDateChange,
    handleCloseDateChange,
    handleSeparateByBedroomChange,
    handleUsePreferencesChange,
    handlePreferenceWeightMethodChange,
    handlePreferenceChange,
  };
}
